import { toJsDate } from "../utils/toJsDate";
import React, { useState, useMemo } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { type ActivityRequest } from "../types";
import { useAuth } from "../contexts/AuthContext";
import {
  useActivityActions,
  useActivityFeedData,
} from "../contexts/ActivityContext";
import { useToast } from "../contexts/ToastContext";
import EditActivityModal from "./EditActivityModal";
import {
  XIcon,
  MessageSquareIcon,
  MoreVerticalIcon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  LogOutIcon,
  Trash2Icon,
  InfoIcon,
  Edit2Icon,
  StarIcon,
  LinkIcon,
} from "./icons/FeatherIcons";
import { CATEGORIES, FEATURE_FLAGS } from "../constants";
import { useCachedImage } from "../hooks/useCachedImage";
import { fallbackImage } from "../utils/getCachedImage";
import { getCategoryDefaultImage } from "../utils/activityUtils";
import { ActivityRichText } from "../utils/activityRichText";

// Category-based default images for better visual experience

const handleImageError =
  (activity: ActivityRequest) =>
  (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.currentTarget;
    const currentFallback = img.getAttribute("data-fallback-level") || "0";
    const seed = activity.id || activity.title || activity.category;

    // First try category-specific default image
    if (currentFallback === "0") {
      img.setAttribute("data-fallback-level", "1");
      img.src = getCategoryDefaultImage(activity.category, seed);
      return;
    }

    // If category image also fails, use basic fallback
    if (currentFallback === "1") {
      img.setAttribute("data-fallback-level", "2");
      img.src = fallbackImage;
    }

    // Stop here - no more fallbacks to prevent infinite loops
  };

// Clean title by removing extra whitespace and HTML entities
const cleanTitle = (title: string) => {
  return title
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};

interface ActivityCardProps {
  activity: ActivityRequest;
  onPass?: (activityId: string) => void;
  hideActions?: boolean;
  viewMode?: "full" | "compact";
  onCloseModal?: () => void;
  isFeedView?: boolean;
  hideDescription?: boolean;
  truncateTitle?: boolean;
}

const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  onPass,
  hideActions = false,
  viewMode = "full",
  onCloseModal,
  isFeedView = false,
  hideDescription = false,
  truncateTitle = true,
}) => {
  const { currentUser } = useAuth();
  const { activities } = useActivityFeedData();
  const {
    joinActivity,
    deleteActivity,
    leaveActivity,
    updateActivity,
    refreshActivities,
  } = useActivityActions();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isPassing, setIsPassing] = useState(false);

  // Use cached profile image for creator
  const creatorProfileImage = useCachedImage(
    activity.creator.profileImageUrl,
    activity.creator.username,
    activity.creator.id,
  );

  // Use cached image for activity image
  const categorySeed = activity.id || activity.title || activity.category;
  const imageSource = useCachedImage(
    activity.imageUrl || getCategoryDefaultImage(activity.category, categorySeed),
    activity.title,
    activity.id,
  );
  const cleanedTitle = cleanTitle(activity.title);

  const { creatorStats, reputationLevel, starRating } = useMemo(() => {
    if (!activities)
      return {
        creatorStats: { created: 0, joined: 0 },
        reputationLevel: 1,
        starRating: 1,
      };

    const creatorId = activity.creator.id;
    const created = activities.filter(
      (a) => !a.isDeleted && a.creator.id === creatorId,
    ).length;
    const joined = activities.filter(
      (a) =>
        !a.isDeleted &&
        a.participants.some((p) => p.id === creatorId) &&
        a.creator.id !== creatorId,
    ).length;

    const level = Math.max(1, Math.floor(Math.sqrt(created * 1.5 + joined)));

    const getStarRating = (lvl: number): number => {
      if (lvl >= 11) return 5;
      if (lvl >= 8) return 4;
      if (lvl >= 5) return 3;
      if (lvl >= 3) return 2;
      return 1;
    };
    const stars = getStarRating(level);

    return {
      creatorStats: { created, joined },
      reputationLevel: level,
      starRating: stars,
    };
  }, [activities, activity.creator.id]);

  const isExpired = useMemo(() => {
    const now = new Date().getTime();
    if (!activity.dateTime || !activity.duration) return false;
    const endTime =
      toJsDate(activity.dateTime).getTime() + activity.duration * 60 * 1000;
    return endTime < now;
  }, [activity.dateTime, activity.duration]);

  const isCompact = viewMode === "compact";

  const categoryInfo = CATEGORIES.find((c) => c.value === activity.category);

  const handleJoin = () => {
    if (!currentUser || isJoining) return;

    setIsJoining(true);

    navigate(`/match/${activity.id}`, {
      state: {
        prefetchedActivity: activity,
      },
    });

    void joinActivity(activity.id)
      .then((result) => {
        if (result.success) {
          if (result.isFull) {
            addToast("Group is full! The chat is now open. 🎉", "success");
          } else {
            addToast("You've joined the activity!", "success");
          }
          return;
        }

        addToast("Failed to join. The activity might be full.", "error");
        navigate("/", { replace: true });
      })
      .catch(() => {
        addToast("Failed to join. Please try again.", "error");
        navigate("/", { replace: true });
      })
      .finally(() => {
        setIsJoining(false);
      });
  };

  const handleLeave = async () => {
    if (!currentUser) return;
    await leaveActivity(activity.id);
    await refreshActivities();
    addToast(`${currentUser.username} has left the activity.`, "info");
    setIsMenuOpen(false);
    navigate("/profile", { state: { initialTab: "declined" } });
  };

  const handleDelete = async () => {
    await deleteActivity(activity.id);
    // Toast is already shown by deleteActivity function in context
    setIsMenuOpen(false);

    // Refresh activities to get the updated state with deletion
    await refreshActivities();

    // If we are on the activity detail page, navigate away
    if (location.pathname === `/activity/${activity.id}`) {
      navigate("/profile", { state: { initialTab: "deleted" } });
    } else {
      // If we're already on profile page, navigate to deleted tab
      navigate("/profile", { state: { initialTab: "deleted" } });
    }
  };

  const handleUpdate = async (updatedData: ActivityRequest) => {
    await updateActivity(updatedData);
    // No need to refresh - updateActivity handles local state updates optimistically
    addToast("Activity updated successfully!", "success");
    setIsEditModalOpen(false);
    setIsMenuOpen(false);
  };

  const handlePass = () => {
    if (onPass) {
      setIsPassing(true);
      Promise.resolve(onPass(activity.id)).finally(() => setIsPassing(false));
    }
  };

  const handleChat = () => {
    // Navigate to chat with readOnly parameter if activity is expired
    const readOnlyParam = isExpired ? "?readonly=true" : "";
    navigate(`/chat/${activity.chatId}${readOnlyParam}`);
  };

  const handleProfileClick = () => {
    if (onCloseModal) {
      onCloseModal();
    }
  };

  const handleCardClick = () => {
    // 1. Get the current path from location (you already have 'location' from useLocation)
    const currentPath = location.pathname;
    const targetPath = `/activity/${activity.id}`;

    // 2. Only navigate if we aren't already there
    if (currentPath === targetPath) {
      return;
    }

    navigate(targetPath, {
      state: {
        prefetchedActivity: activity,
      },
    });
  };

  if (!currentUser) return null;

  const isParticipant = activity.participants.some(
    (p) => p.id === currentUser.id,
  );
  const isCreator = activity.creator.id === currentUser.id;
  const isFull = activity.participants.length >= activity.groupSize;
  const hasPendingRejoinRequest =
    activity.pendingRejoinRequests?.includes(currentUser.id) || false;

  const timeAgo = (dateInput: any) => {
    const date = toJsDate(dateInput);
    if (!date || isNaN(date.getTime())) return "N/A";
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return `${Math.floor(interval)}y ago`;
    interval = seconds / 2592000;
    if (interval > 1) return `${Math.floor(interval)}mo ago`;
    interval = seconds / 86400;
    if (interval > 1) return `${Math.floor(interval)}d ago`;
    interval = seconds / 3600;
    if (interval > 1) return `${Math.floor(interval)}h ago`;
    interval = seconds / 60;
    if (interval > 1) return `${Math.floor(interval)}m ago`;
    return `${Math.floor(seconds)}s ago`;
  };

  const formatDate = (dateString: string) => {
    const date = toJsDate(dateString);
    if (!date || isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDuration = (minutes: number) => {
    if (!minutes || minutes <= 0) return "";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    let result = "";
    if (h > 0) result += `${h}h`;
    if (m > 0) result += `${h > 0 ? " " : ""}${m}m`;
    return result;
  };

  const formatDeletedDate = (dateString: string) => {
    const date = toJsDate(dateString);
    if (!date || isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Share handler (same logic as ActivityPage)
  const handleShare = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      const shareUrl = `https://app.whoisin.sg/app-redirect.html?activity=${activity.id}`;
      const shareText = `Check out this activity: ${activity.title}`;
      try {
        const { Share } = await import("@capacitor/share");
        await Share.share({
          title: activity.title,
          text: shareText,
          url: shareUrl,
          dialogTitle: "Share activity",
        });
      } catch (capacitorError) {
        if (
          capacitorError?.message !== "Share canceled" &&
          capacitorError?.name !== "AbortError"
        ) {
          if (navigator.share) {
            await navigator.share({
              title: activity.title,
              text: shareText,
              url: shareUrl,
            });
          } else if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(shareUrl);
            addToast("Link copied to clipboard!", "success");
          }
        }
      }
    } catch (error: any) {
      if (error?.message !== "Share canceled" && error?.name !== "AbortError") {
        addToast("Failed to share activity", "error");
      }
    }
  };

  return (
    <>
      {isCreator && isEditModalOpen && (
        <EditActivityModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          activity={activity}
          onSave={handleUpdate}
        />
      )}
      <div
        className="bg-white overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={handleCardClick}
      >
        {activity.isDeleted && (
          <div className="bg-yellow-100/80 text-yellow-800 text-sm font-semibold p-3 text-center flex items-center justify-center gap-2">
            <InfoIcon className="w-5 h-5" />
            Deleted on{" "}
            {activity.deletedAt ? formatDeletedDate(activity.deletedAt) : "N/A"}
          </div>
        )}
        {isExpired && !activity.isDeleted && (
          <div className="bg-gray-100 text-gray-500 text-sm font-semibold p-3 text-center flex items-center justify-center gap-2">
            <ClockIcon className="w-5 h-5" />
            This activity has ended.
          </div>
        )}
        {/* Header */}
        <div className="p-3 flex items-center justify-between gap-3">
          <Link
            to={`/profile/${activity.creator.id}`}
            onClick={(e) => {
              e.stopPropagation();
              handleProfileClick();
            }}
            className="flex items-center space-x-3 group flex-1 min-w-0"
          >
            <img
              loading="lazy"
              onError={handleImageError(activity)}
              src={creatorProfileImage}
              alt={activity.creator.username}
              className="w-10 h-10 rounded-2xl flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-gray-900 group-hover:underline truncate">
                {activity.creator.username}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                created {timeAgo(activity.createdAt)}
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-3 flex-shrink-0">
            {FEATURE_FLAGS.starRating && (
              <div
                className="flex items-center"
                title={`Engagement Level: ${reputationLevel} (${creatorStats.created} created, ${creatorStats.joined} joined)`}
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`w-4 h-4 ${i < starRating ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                  />
                ))}
              </div>
            )}
            {!hideActions &&
              (isCreator || (isParticipant && !isCreator)) &&
              !activity.isDeleted &&
              !isExpired && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMenuOpen(!isMenuOpen);
                    }}
                    className="p-1 rounded-full hover:bg-gray-100"
                  >
                    <MoreVerticalIcon className="w-5 h-5 text-gray-500" />
                  </button>
                  {isMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-1">
                      {isCreator && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsEditModalOpen(true);
                              setIsMenuOpen(false);
                            }}
                            className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Edit2Icon className="w-4 h-4 mr-2" />
                            Edit Activity
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete();
                            }}
                            className="flex items-center w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2Icon className="w-4 h-4 mr-2" />
                            Delete Activity
                          </button>
                        </>
                      )}
                      {isParticipant && !isCreator && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeave();
                          }}
                          className="flex items-center w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-500/10"
                        >
                          <LogOutIcon className="w-4 h-4 mr-2" />
                          Leave Activity
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        {/* Image with Category Overlay */}
        <div className="relative">
          <img
            loading="lazy"
            onError={handleImageError(activity)}
            className={`w-full object-cover ${isCompact ? "h-40" : "h-48"}`}
            style={{ aspectRatio: isCompact ? "16 / 9" : "4 / 3" }}
            src={imageSource}
            alt={cleanedTitle}
          />
          {categoryInfo && (
            <div className="absolute top-2 right-2 bg-black/20 text-white text-xs px-2 py-1 rounded-md flex items-center backdrop-blur-sm">
              <categoryInfo.Icon className="w-3 h-3 mr-1" />
              <span className="font-medium">{categoryInfo.name}</span>
            </div>
          )}
        </div>

        {/* Content Body */}
        <div className="p-4 space-y-3">
          <div className="flex justify-between items-start gap-3">
            <div className="flex items-center flex-1 min-w-0">
              <h3
                className={`font-bold text-xl text-gray-900 ${isFeedView ? "line-clamp-1" : ""} flex-1 ${truncateTitle ? "truncate" : ""}`}
                title={cleanedTitle}
              >
                {cleanedTitle}
              </h3>
              <button
                onClick={handleShare}
                className="ml-2 text-gray-600 hover:text-primary p-1.5 rounded-full transition-colors flex-shrink-0"
                title="Share activity"
              >
                <LinkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          {!hideDescription && activity.description && (
            <p
              className={`text-sm text-gray-500 leading-relaxed ${isFeedView ? "line-clamp-3" : ""}`}
            >
              <ActivityRichText text={activity.description} />
            </p>
          )}

          <div className="pt-2 space-y-2 text-sm text-gray-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 mr-2 overflow-hidden">
                <MapPinIcon className="w-4 h-4 mr-3 text-primary/70 flex-shrink-0" />
                <span className="font-medium text-gray-900 truncate">
                  {activity.location}
                </span>
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.location + ", Singapore")}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs font-bold text-primary hover:underline flex-shrink-0"
              >
                Get direction
              </a>
            </div>
            <div className="flex items-center">
              <ClockIcon className="w-4 h-4 mr-3 text-primary/70" />
              <span className="font-medium text-gray-900">
                {formatDate(activity.dateTime)}
              </span>
              {activity.duration > 0 && (
                <span className="ml-2 text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {formatDuration(activity.duration)}
                </span>
              )}
            </div>
            <div className="flex items-center">
              <UsersIcon className="w-4 h-4 mr-3 text-primary/70" />
              <span className="font-medium text-gray-900">
                {activity.participants.length} / {activity.groupSize} Joined
              </span>
            </div>
          </div>
        </div>

        {/* Footer with actions */}
        {!hideActions && !activity.isDeleted && (
          <div className="px-4 pb-4 pt-3">
            {isExpired ? (
              isParticipant && activity.participants.length > 1 ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChat();
                  }}
                  className="w-full bg-gray-600 text-white font-bold py-3 px-6 rounded-full flex items-center justify-center shadow-lg shadow-gray-600/30 transform hover:scale-105 transition-transform"
                >
                  <MessageSquareIcon className="w-5 h-5 mr-2" /> View Chat
                  History
                </button>
              ) : (
                <div className="w-full text-center bg-gray-100 text-gray-500 font-semibold py-3 px-6 rounded-full">
                  Activity Ended
                </div>
              )
            ) : (
              <div className="flex items-center w-full">
                {isParticipant ? (
                  activity.participants.length > 1 ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChat();
                      }}
                      className="w-full bg-primary text-white font-bold py-3 px-6 rounded-full flex items-center justify-center shadow-lg shadow-primary/30 transform hover:scale-105 transition-transform"
                    >
                      <MessageSquareIcon className="w-5 h-5 mr-2" /> Group Chat
                    </button>
                  ) : (
                    <div className="w-full text-center bg-gray-100 text-gray-500 font-semibold py-3 px-6 rounded-full">
                      Waiting for others to join...
                    </div>
                  )
                ) : (
                  <div className="flex space-x-3 items-center w-full">
                    {onPass && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePass();
                        }}
                        aria-label="Decline this activity"
                        disabled={isPassing}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
                          isPassing
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                      >
                        {isPassing ? (
                          <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <XIcon className="w-7 h-7" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJoin();
                      }}
                      disabled={isFull || hasPendingRejoinRequest || isJoining}
                      aria-label="Join this activity"
                      className={`flex-1 font-bold rounded-full py-3 text-lg transition-all transform shadow-lg disabled:shadow-none disabled:scale-100 disabled:cursor-not-allowed ${
                        hasPendingRejoinRequest
                          ? "bg-yellow-500 text-white"
                          : "bg-primary text-white shadow-primary/40 hover:opacity-90 hover:scale-105 disabled:bg-gray-400"
                      }`}
                    >
                      {hasPendingRejoinRequest
                        ? "Pending"
                        : isFull
                          ? "Full"
                          : isJoining
                            ? "Joining..."
                            : "Join"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ActivityCard;
